import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Link,
  Box,
  Divider,
  useTheme,
  Stack,
} from "@mui/material";
import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config/envConfig";

interface Category {
  id: string;
  name: string;
  slug: string;
}

const Footer: React.FC = () => {
  const theme = useTheme();
  const year = new Date().getFullYear();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    // Fetch categories from API
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/categories`);
        if (response.data.success) {
          setCategories(response.data.data.slice(0, 5)); // Display at most 5 categories
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.common.white,
        py: { xs: 6, md: 8 },
        mt: "auto",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 6, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Hakkımızda bölümü */}
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 3, fontWeight: "bold" }}
            >
              Akademi Kadro
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.8 }}>
              Güncel haberler, son dakika gelişmeleri ve tüm kategorilerde en
              güncel içerikleri sunan haber portalı.
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mb: { xs: 3, md: 0 } }}>
              <Link
                href="https://x.com/akademi_kadro"
                target="_blank"
                rel="noopener"
                color="inherit"
                aria-label="X.com"
                sx={{
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.2)" },
                }}
              >
                <XIcon />
              </Link>
              <Link
                href="https://www.instagram.com/akademikadronet/"
                target="_blank"
                rel="noopener"
                color="inherit"
                aria-label="Instagram"
                sx={{
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.2)" },
                }}
              >
                <InstagramIcon />
              </Link>
              <Link
                href="https://www.facebook.com/AkademiKadro.Net"
                target="_blank"
                rel="noopener"
                color="inherit"
                aria-label="Facebook"
                sx={{
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.2)" },
                }}
              >
                <FacebookIcon />
              </Link>
            </Stack>
          </Box>

          {/* Kategoriler bölümü */}
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 3, fontWeight: "bold" }}
            >
              Kategoriler
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {categories.map((category) => (
                <Box component="li" sx={{ mb: 2 }} key={category.id}>
                  <Link
                    component={RouterLink}
                    to={`/category/${category.id}`}
                    color="inherit"
                    underline="hover"
                    sx={{
                      transition: "all 0.2s",
                      "&:hover": { pl: 1, color: "primary.light" },
                    }}
                  >
                    {category.name}
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Kurumsal bölümü */}
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 3, fontWeight: "bold" }}
            >
              Kurumsal
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/about"
                  color="inherit"
                  underline="hover"
                  sx={{
                    transition: "all 0.2s",
                    "&:hover": { pl: 1, color: "primary.light" },
                  }}
                >
                  Hakkımızda
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/contact"
                  color="inherit"
                  underline="hover"
                  sx={{
                    transition: "all 0.2s",
                    "&:hover": { pl: 1, color: "primary.light" },
                  }}
                >
                  İletişim
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/privacy-policy"
                  color="inherit"
                  underline="hover"
                  sx={{
                    transition: "all 0.2s",
                    "&:hover": { pl: 1, color: "primary.light" },
                  }}
                >
                  Gizlilik Politikası
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/terms-of-service"
                  color="inherit"
                  underline="hover"
                  sx={{
                    transition: "all 0.2s",
                    "&:hover": { pl: 1, color: "primary.light" },
                  }}
                >
                  Kullanım Koşulları
                </Link>
              </Box>
            </Box>
          </Box>

          {/* İletişim bölümü */}
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mb: 3, fontWeight: "bold" }}
            >
              İletişim
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  <strong>Adres:</strong>
                  <br />
                  Çankaya/Ankara
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  <strong>E-posta:</strong>
                  <br />
                  akademikadro@gmail.com
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            my: { xs: 4, md: 6 },
            borderColor: "rgba(255, 255, 255, 0.12)",
          }}
        />

        <Box
          sx={{
            textAlign: "center",
            py: { xs: 2, md: 3 },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.9rem" }}
          >
            &copy; {year} Akademi Kadro. Tüm hakları saklıdır.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
